
import { 
  collection, doc, setDoc, getDocs, deleteDoc, updateDoc,
  onSnapshot, query, orderBy, where, increment, getDoc
} from "firebase/firestore";
import { dbFirestore } from "./firebase";
import { User, Transaction, PayoutRequest, SystemSettings, TransactionStatus, Role } from '../types';
import { DEFAULT_SETTINGS, MOCK_USERS } from '../constants';

// Collection Names
const COLL = {
  USERS: 'users',
  TRANSACTIONS: 'transactions',
  PAYOUTS: 'payouts',
  SETTINGS: 'settings',
};

// Helper: Generate 6 digit string
const generateMockCode = () => Math.floor(100000 + Math.random() * 900000).toString();

// --- HELPER: Initial Seeding ---
const seedDataIfEmpty = async () => {
    const usersSnap = await getDocs(collection(dbFirestore, COLL.USERS));
    
    // If empty, seed everything
    if (usersSnap.empty) {
        console.log("🌱 Seeding Mock Data to Firebase...");
        for (const user of MOCK_USERS) {
            await setDoc(doc(dbFirestore, COLL.USERS, user.id), user);
            console.log(`✅ Seeded user: ${user.email}`);
        }
        await setDoc(doc(dbFirestore, COLL.SETTINGS, 'general_settings'), DEFAULT_SETTINGS);
        console.log("✅ Seeding completed");
    } else {
        console.log(`ℹ️ Database already has ${usersSnap.size} users. Skipping seed.`);
    }
};

seedDataIfEmpty();

// Database Service - Realtime Version
export const db = {
  // --- REALTIME LISTENERS ---
  
  listenToUsers: (callback: (users: User[]) => void) => {
    const q = query(collection(dbFirestore, COLL.USERS));
    return onSnapshot(q, (snapshot) => {
      const users: User[] = [];
      snapshot.forEach((doc) => users.push(doc.data() as User));
      callback(users);
    });
  },

  // Force update admin user with correct password (for debugging)
  forceUpdateAdmin: async () => {
    const adminUser = MOCK_USERS[0]; // Get admin from constants
    await setDoc(doc(dbFirestore, COLL.USERS, adminUser.id), adminUser);
    console.log("✅ Admin user force updated with password:", adminUser.password);
  },

  listenToTransactions: (callback: (txns: Transaction[]) => void) => {
    const q = query(collection(dbFirestore, COLL.TRANSACTIONS), orderBy("date", "desc"));
    return onSnapshot(q, (snapshot) => {
      const txns: Transaction[] = [];
      snapshot.forEach((doc) => txns.push(doc.data() as Transaction));
      callback(txns);
    });
  },

  listenToPayouts: (callback: (payouts: PayoutRequest[]) => void) => {
     const q = query(collection(dbFirestore, COLL.PAYOUTS), orderBy("date", "desc"));
    return onSnapshot(q, (snapshot) => {
      const payouts: PayoutRequest[] = [];
      snapshot.forEach((doc) => payouts.push(doc.data() as PayoutRequest));
      callback(payouts);
    });
  },

  listenToSettings: (callback: (settings: SystemSettings) => void) => {
      return onSnapshot(doc(dbFirestore, COLL.SETTINGS, 'general_settings'), (doc) => {
          if (doc.exists()) {
              callback(doc.data() as SystemSettings);
          } else {
              callback(DEFAULT_SETTINGS);
          }
      });
  },

  // --- ACTIONS ---

  saveUser: async (user: User) => {
    await setDoc(doc(dbFirestore, COLL.USERS, user.id), user);
  },
  
  updateUserStats: async (userId: string, data: Partial<User>) => {
      const ref = doc(dbFirestore, COLL.USERS, userId);
      await updateDoc(ref, data);
  },

  incrementUserClick: async (referralCode: string) => {
      // Find user by referral code first
      const q = query(collection(dbFirestore, COLL.USERS), where("referralCode", "==", referralCode));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          const today = new Date().toISOString().split('T')[0];
          
          await updateDoc(userDoc.ref, {
              clickCount: increment(1),
              [`dailyStats.${today}.clicks`]: increment(1)
          });
          return userDoc.data() as User; // Return the affiliate found
      }
      return null;
  },

  incrementReferralCount: async (affiliateId: string) => {
      await updateDoc(doc(dbFirestore, COLL.USERS, affiliateId), {
          totalReferrals: increment(1)
      });
  },

  deleteUser: async (id: string) => {
    await deleteDoc(doc(dbFirestore, COLL.USERS, id));
  },

  createTransaction: async (txn: Transaction) => {
    await setDoc(doc(dbFirestore, COLL.TRANSACTIONS, txn.id), txn);

    // If transaction belongs to an affiliate, update their daily sales stats
    if (txn.affiliateId) {
        const today = txn.date; // Use transaction date
        const affRef = doc(dbFirestore, COLL.USERS, txn.affiliateId);
        
        await updateDoc(affRef, {
            totalSales: increment(txn.amount),
            [`dailyStats.${today}.sales`]: increment(1)
        });
    }
  },

  savePayout: async (payout: PayoutRequest) => {
    await setDoc(doc(dbFirestore, COLL.PAYOUTS, payout.id), payout);
  },

  saveSettings: async (settings: SystemSettings) => {
    await setDoc(doc(dbFirestore, COLL.SETTINGS, 'general_settings'), settings);
  },

  saveSession: (userId: string) => localStorage.setItem('sv_session_uid', userId),
  getSession: () => localStorage.getItem('sv_session_uid'),
  clearSession: () => localStorage.removeItem('sv_session_uid'),

  reset: async () => {
     alert("Chức năng Reset bị tắt để bảo vệ dữ liệu.");
  }
};
